import { useEffect, useState } from 'react';

import { useMenuBodyLang } from './useMenuBodyLang';
import { menuPageService } from 'features/menu-page/infra';
import { useMenuBodySnackBar } from './useMenuBodySnackBar';

import { MIN_LENGTH_SEARCH_QUERY } from 'features/menu-page/domain';

import type {
  FullMenuCatalog,
  IMenuCustomization,
  MenuOutOfStock,
  IMenuProduct,
  ITurnOffProduct,
  ITurnOffCustomization,
} from 'features/menu-page/domain';
import type { CustomizationId, StoreId } from 'shared/domain';

type NavOpt = 'outOfStock' | 'products' | 'customGroups' | 'searchResults';

const NAV_OPT: Record<NavOpt, NavOpt> = {
  outOfStock: 'outOfStock',
  products: 'products',
  customGroups: 'customGroups',
  searchResults: 'searchResults',
} as const;

const INIT_CUSTOM_TO_TURN_OFF_LIST = {
  list: [],
  customDate: new Date(),
  shutdownTimeOption: 'DAILY',
} as _CustomizationToTurnOffList;

export function useMenuBody(props: Props) {
  const {
    loading,
    storeId,
    searchQuery,
    catalog,
    outOfStock,
    refreshCatalog,
  } = props;

  const showSearchResultsBtn = searchQuery.length >= MIN_LENGTH_SEARCH_QUERY;

  const [navOpt, setNavOpt] = useState<NavOpt>(NAV_OPT.products);
  const [productToTurnOff, setProductToTurnOff] = useState<IMenuProduct | null>(
    null,
  );
  const [
    customizationToTurnOff,
    setCustomizationToTurnOff,
  ] = useState<IMenuCustomization | null>(null);
  const [customizationToTurnOffList, setCustomizationToTurnOffList] = useState<
    _CustomizationToTurnOffList
  >(INIT_CUSTOM_TO_TURN_OFF_LIST);

  const isNavOutOfStock = navOpt === NAV_OPT.outOfStock && !loading;
  const isNavProducts = navOpt === NAV_OPT.products && !loading;
  const isNavCustomGroups = navOpt === NAV_OPT.customGroups && !loading;
  const isNavSearchResults = navOpt === NAV_OPT.searchResults && !loading;

  const {
    turnOnProduct,
    turnOffProduct,
    turnOnCustomization,
    turnOffCustomization,
    turnOffCustomizationList,
    someDialogOpened,
  } = menuPageService();

  const menuPageDialogOpened = someDialogOpened();

  const snackBar = useMenuBodySnackBar();

  const lang = useMenuBodyLang({ quantityOutOfStock: outOfStock.quantity });

  const handleShowOutOfStock = () => setNavOpt(NAV_OPT.outOfStock);
  const handleShowProducts = () => setNavOpt(NAV_OPT.products);
  const handleShowCustomGroups = () => setNavOpt(NAV_OPT.customGroups);
  const handleShowSearchResults = () => setNavOpt(NAV_OPT.searchResults);

  const _resetTurnOffSelected = () => {
    const hasCustomToTurnOff = customizationToTurnOff !== null;
    if (productToTurnOff !== null) setProductToTurnOff(null);
    if (hasCustomToTurnOff) setCustomizationToTurnOff(null);
    if (!hasCustomToTurnOff && customizationToTurnOffList.list.length > 0) {
      setCustomizationToTurnOffList(INIT_CUSTOM_TO_TURN_OFF_LIST);
    }
  };

  const _refreshCatalog = () => {
    _resetTurnOffSelected();
    setTimeout(refreshCatalog, 200);
  };

  const _handleTurnOnItemSuccess = () => {
    _refreshCatalog();
    snackBar.turnOnItemSuccess();
  };

  const toggleProduct = (checked: boolean, product: IMenuProduct) => {
    if (checked) {
      turnOnProduct({ storeId, productId: product.id })
        .then(_handleTurnOnItemSuccess)
        .catch(() => snackBar.turnOnOffItemError(true));
    } else {
      setProductToTurnOff(product);
    }
  };

  const toggleCustomization = (
    checked: boolean,
    custom: IMenuCustomization,
  ) => {
    if (checked) {
      turnOnCustomization({ storeId, customizationId: custom.id })
        .then(_handleTurnOnItemSuccess)
        .catch(() => snackBar.turnOnOffItemError(true));
    } else {
      setCustomizationToTurnOff(custom);
    }
  };

  const turnOffProductCTA = (params: TurnOffProductCTAParams) => {
    const { shutdownTimeOption, customDate } = params;
    if (productToTurnOff !== null) {
      turnOffProduct({
        storeId,
        customDate,
        shutdownTimeOption,
        productId: productToTurnOff.id,
      })
        .then(() => {
          _refreshCatalog();
          snackBar.turnOffProductSuccess({ shutdownTimeOption, customDate });
        })
        .catch(snackBar.turnOnOffItemError);
    }
  };

  const turnOffCustomizationCTA = (params: TurnOffCustomizationCTAParams) => {
    const { shutdownTimeOption, customDate } = params;
    if (customizationToTurnOff !== null) {
      turnOffCustomization({
        storeId,
        customDate,
        shutdownTimeOption,
        customizationId: customizationToTurnOff.id,
      })
        .then(() => {
          _refreshCatalog();
          snackBar.turnOffCustomSuccess({ shutdownTimeOption, customDate });
          const cDescription = customizationToTurnOff.description.toLowerCase();
          setCustomizationToTurnOffList({
            customDate,
            shutdownTimeOption,
            list: catalog.customizationGroups.reduce(
              (acc, cv) => [
                ...acc,
                ...cv.items.filter(
                  c =>
                    c.isOn &&
                    c.id !== customizationToTurnOff.id &&
                    c.description.toLowerCase().includes(cDescription),
                ),
              ],
              [] as IMenuCustomization[],
            ),
          });
        })
        .catch(snackBar.turnOnOffItemError);
    }
  };

  const turnOffCustomizationListCTA = (customizationIds: CustomizationId[]) => {
    const { customDate, shutdownTimeOption } = customizationToTurnOffList;
    turnOffCustomizationList({
      storeId,
      customDate,
      shutdownTimeOption,
      customizationIds,
    })
      .then(() => {
        _refreshCatalog();
        snackBar.turnOffCustomSuccess({ shutdownTimeOption, customDate });
      })
      .catch(snackBar.turnOnOffItemError);
  };

  useEffect(() => {
    if (showSearchResultsBtn) handleShowSearchResults();
    if (searchQuery.length === 0 && isNavSearchResults) handleShowProducts();
  }, [searchQuery]);

  useEffect(() => {
    if (outOfStock.quantity === 0 && isNavOutOfStock) handleShowProducts();
  }, [outOfStock]);

  useEffect(() => {
    if (
      productToTurnOff !== null ||
      customizationToTurnOff !== null ||
      customizationToTurnOffList.list.length > 0
    )
      menuPageDialogOpened.dispatch(true);
    else menuPageDialogOpened.dispatch(false);
  }, [productToTurnOff, customizationToTurnOff, customizationToTurnOffList]);

  return {
    lang,
    showOutOfStockBtn: outOfStock.quantity !== 0,
    showSearchResultsBtn,
    isNavOutOfStock,
    isNavProducts,
    isNavCustomGroups,
    isNavSearchResults,
    outOfStock,
    productToTurnOff,
    customizationToTurnOff,
    customizationToTurnOffList,
    handleShowOutOfStock,
    handleShowProducts,
    handleShowCustomGroups,
    handleShowSearchResults,
    toggleProduct,
    toggleCustomization,
    turnOffProductCTA,
    turnOffCustomizationCTA,
    turnOffCustomizationListCTA,
    cancelTurnOffCTA: _resetTurnOffSelected,
  };
}

interface Props {
  storeId: StoreId;
  loading: boolean;
  searchQuery: string;
  catalog: FullMenuCatalog;
  outOfStock: MenuOutOfStock;
  refreshCatalog: () => void;
}

type TurnOffProductCTAParams = Pick<
  ITurnOffProduct,
  'shutdownTimeOption' | 'customDate'
>;

type TurnOffCustomizationCTAParams = Pick<
  ITurnOffCustomization,
  'shutdownTimeOption' | 'customDate'
>;

interface _CustomizationToTurnOffList extends TurnOffCustomizationCTAParams {
  list: IMenuCustomization[];
}
